import { useCallback, useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

/**
 * The CAPTCHA token Supabase wants on every auth call.
 *
 * Two things about this are easy to get wrong and expensive when you do.
 *
 * **It is all or nothing.** Supabase applies CAPTCHA to sign-in, sign-up AND
 * password reset the moment it is switched on, and to every client at once.
 * A client that does not send a token cannot authenticate at all — so this
 * ships and is verified working *before* the switch is flipped, not after.
 *
 * **A token is single-use.** Cloudflare rejects a replayed one, so a failed
 * sign-in must fetch a fresh token before the next attempt. Hence `reset`,
 * which every caller runs after an error.
 *
 * With no site key configured the widget is not rendered and the token is
 * undefined, which Supabase ignores while CAPTCHA is off. That is the state
 * this ships in.
 *
 * **It cannot be verified from an automated browser.** Turnstile answers one
 * with error 600010 — a challenge failure on suspected bot behaviour — which
 * is the product working, not a misconfiguration. Proving this end to end
 * needs a person in an ordinary browser.
 */

const SITE_KEY = import.meta.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY as string | undefined;

export function useTurnstile() {
  const [token, setToken] = useState<string | undefined>(undefined);
  const ref = useRef<TurnstileInstance | null>(null);

  const reset = useCallback(() => {
    setToken(undefined);
    ref.current?.reset();
  }, []);

  // Rendered near the submit button. Invisible mode draws nothing visible and
  // only interrupts a request that looks automated — which matters on the
  // phone, where the person is at a gate trying to start work, but the same
  // widget is used here so both clients behave alike.
  const widget = SITE_KEY ? (
    <Turnstile
      ref={ref}
      siteKey={SITE_KEY}
      // No size option. Invisibility is a property of the widget on
      // Cloudflare — this one was created with --mode invisible — and `size`
      // only accepts normal, flexible and compact, so "invisible" was never a
      // valid value to pass here.
      onSuccess={setToken}
      onExpire={() => setToken(undefined)}
      onError={() => setToken(undefined)}
    />
  ) : null;

  return { token, reset, widget, enabled: Boolean(SITE_KEY) };
}
